import _ from 'lodash';
import { sqDurationStore } from '@/core/core.stores';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import {
  DEFAULT_ANOMALY_THRESHOLD,
  DEFAULT_MONITORING_CONTRAST,
  DEFAULT_NUM_COMPONENTS,
  OutputSignalType,
  OutputType,
} from '@/tools/principalComponentAnalysis/principalComponentAnalysis.constants';
import type { BaseToolPersistedState, ToolConfigParams, ToolItem, ToolParameter } from '@/toolSelection/baseTool.store';
import type { ValueWithUnitsItem } from '@/trend/ValueWithUnits.atom';
import type { Capsule } from '@/utilities/datetime.constants';

export const DEFAULT_DURATION = { value: 0, units: 'min' };

interface PrincipalComponentAnalysisState extends BaseToolPersistedState {
  windowStart: number;
  windowEnd: number;
  window: any; // any because of Baobab's `monkey()`
  modelPropertiesExpanded: boolean;
  color: string;
  numComponents: number;
  anomalyThreshold: number;
  monitoringContrast: number;
  outputType: OutputType;
  outputSignalType: OutputSignalType;
  cleanCapsules: boolean;
  capsuleMinDuration: ValueWithUnitsItem;
  capsuleMergeDuration: ValueWithUnitsItem;
  inputSignals: ToolItem[];
  condition?: ToolItem;
  restrictToCondition?: ToolItem;
}

export type PrincipalComponentAnalysisDehydratedState = Omit<PrincipalComponentAnalysisState, 'window'>;

export class PrincipalComponentAnalysisStore extends BaseToolStore {
  static readonly storeName = 'sqPrincipalComponentAnalysisStore';
  type = TREND_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS;
  parameterDefinitions = {
    condition: {
      predicate: ['name', 'condition'],
    },
    restrictToCondition: {
      predicate: ['name', 'restrictToCondition'],
    },
    inputSignals: {
      predicate: (parameter: ToolParameter) =>
        parameter?.name !== 'condition' && parameter?.name !== 'restrictToCondition',
      multiple: true,
    },
  };

  initialize(): void {
    const initialState: PrincipalComponentAnalysisState = {
      ...BASE_TOOL_COMMON_PROPS,
      windowStart: sqDurationStore.getState().displayRange.start,
      windowEnd: sqDurationStore.getState().displayRange.end,
      window: this.monkey(['windowStart'], ['windowEnd'], (startTime, endTime) => ({ startTime, endTime })),
      modelPropertiesExpanded: false,
      color: '',
      numComponents: DEFAULT_NUM_COMPONENTS,
      anomalyThreshold: DEFAULT_ANOMALY_THRESHOLD,
      monitoringContrast: DEFAULT_MONITORING_CONTRAST,
      outputType: OutputType.CONDITION,
      outputSignalType: OutputSignalType.LOGLIKE,
      cleanCapsules: false,
      capsuleMinDuration: DEFAULT_DURATION,
      capsuleMergeDuration: DEFAULT_DURATION,
      condition: undefined,
      restrictToCondition: undefined,
      inputSignals: [],
    };
    this.state = this.immutable(initialState);
  }

  get inputSignals(): ToolItem[] {
    return this.state.get('inputSignals');
  }

  get condition(): ToolItem | undefined {
    return this.state.get('condition');
  }

  get restrictToCondition(): ToolItem | undefined {
    return this.state.get('restrictToCondition');
  }

  get window(): { startTime: number; endTime: number } {
    return this.state.get('window');
  }

  get color(): string {
    return this.state.get('color');
  }

  get modelPropertiesExpanded(): boolean {
    return this.state.get('modelPropertiesExpanded');
  }

  get numComponents(): number {
    return this.state.get('numComponents');
  }

  get anomalyThreshold(): number {
    return this.state.get('anomalyThreshold');
  }

  get monitoringContrast(): number {
    return this.state.get('monitoringContrast');
  }

  get outputType(): OutputType {
    return this.state.get('outputType');
  }

  get outputSignalType(): OutputSignalType {
    return this.state.get('outputSignalType');
  }

  get cleanCapsules(): boolean {
    return this.state.get('cleanCapsules');
  }

  get capsuleMinDuration(): ValueWithUnitsItem {
    return this.state.get('capsuleMinDuration');
  }

  get capsuleMergeDuration(): ValueWithUnitsItem {
    return this.state.get('capsuleMergeDuration');
  }

  dehydrate(): PrincipalComponentAnalysisDehydratedState {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: Partial<PrincipalComponentAnalysisDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  setTrainingWindow(payload: { window: Capsule }): void {
    this.state.set('windowStart', _.get(payload.window, 'startTime'));
    this.state.set('windowEnd', _.get(payload.window, 'endTime'));
  }

  setColor(payload: { color: string }): void {
    this.state.set('color', payload.color);
  }

  setModelPropertiesExpanded(payload: { modelPropertiesExpanded: boolean }): void {
    this.state.set('modelPropertiesExpanded', payload.modelPropertiesExpanded);
  }

  setNumComponents(payload: { numComponents: number }): void {
    this.state.set('numComponents', payload.numComponents);
  }

  setAnomalyThreshold(payload: { anomalyThreshold: number }): void {
    this.state.set('anomalyThreshold', payload.anomalyThreshold);
  }

  setMonitoringContrast(payload: { monitoringContrast: number }): void {
    this.state.set('monitoringContrast', payload.monitoringContrast);
  }

  setOutputType(payload: { outputType: OutputType }): void {
    this.state.set('outputType', payload.outputType);
  }

  setOutputSignalType(payload: { outputSignalType: OutputSignalType }): void {
    this.state.set('outputSignalType', payload.outputSignalType);
  }

  setCleanCapsules(payload: { cleanCapsules: boolean }): void {
    this.state.set('cleanCapsules', payload.cleanCapsules);
  }

  setCapsuleMinDuration(payload: { capsuleMinDuration: ValueWithUnitsItem }): void {
    this.state.set('capsuleMinDuration', payload.capsuleMinDuration);
  }

  setCapsuleMergeDuration(payload: { capsuleMergeDuration: ValueWithUnitsItem }): void {
    this.state.set('capsuleMergeDuration', payload.capsuleMergeDuration);
  }

  override modifyConfigParams<TConfig extends ToolConfigParams>(config: TConfig): TConfig {
    return _.omit(config, ['modelPropertiesExpanded']) as TConfig;
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    PCA_SET_TRAINING_WINDOW: this.setTrainingWindow,
    PCA_SET_COLOR: this.setColor,
    PCA_SET_MODEL_PROPERTIES_EXPANDED: this.setModelPropertiesExpanded,
    PCA_SET_NUM_COMPONENTS: this.setNumComponents,
    PCA_SET_ANOMALY_THRESHOLD: this.setAnomalyThreshold,
    PCA_SET_MONITORING_CONTRAST: this.setMonitoringContrast,
    PCA_SET_OUTPUT_TYPE: this.setOutputType,
    PCA_SET_OUTPUT_SIGNAL_TYPE: this.setOutputSignalType,
    PCA_SET_CLEAN_CAPSULES: this.setCleanCapsules,
    PCA_SET_CAPSULE_MIN_DURATION: this.setCapsuleMinDuration,
    PCA_SET_CAPSULE_MERGE_DURATION: this.setCapsuleMergeDuration,
  };
}
